import type { CropperRef } from 'react-advanced-cropper';
import { create } from 'zustand';
import type { OmitFunctions } from '@/types';
import { createSelectors } from './selector';

interface ImageState {
  image: HTMLImageElement | null;
  backgroundColor: string;
  customColor: string;
  rotation: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  previewCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  isCropping: boolean;

  setIsCropping: (isCropping: boolean) => void;
  setImage: (img: HTMLImageElement | null) => void;
  setBackgroundColor: (color: string) => void;
  setCustomColor: (color: string) => void;
  rotateLeft: () => void;
  rotateRight: () => void;
  flipH: () => void;
  flipV: () => void;
  loadImage: (imageSource: File | string) => void;
  resetTransforms: () => void;
  controls: CropperRef | null;
  setControls: (controls: CropperRef | null) => void;
  onCropApply: () => void;
  setImageCropperBackground: (color?: string) => void;
  getImageResult: () => Promise<File | null> | undefined;
}

const initialState: OmitFunctions<ImageState> = {
  image: null,
  controls: null,
  backgroundColor: 'transparent',
  customColor: '#0000ff',
  rotation: 0,
  flipHorizontal: false,
  flipVertical: false,
  canvasRef: { current: null },
  previewCanvasRef: { current: null },
  isCropping: false,
};

export const imageSlice = create<ImageState>((set, get) => ({
  ...initialState,
  reset: () => {
    set(initialState);
  },
  setControls: (controls) => set({ controls }),
  setImageCropperBackground: (bgColor) => {
    const color = bgColor ?? get().backgroundColor;
    const el = document.querySelector('.advanced-cropper-background-image.advanced-cropper__background');
    if (el?.parentElement) el.parentElement.style.background = color;
  },
  getImageResult: () => {
    const canvas = get().controls?.getCanvas();
    if (!canvas) return undefined;

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) return resolve(null);
          if (blob instanceof File) {
            resolve(blob);
            return;
          }
          const file = new File([blob], 'image.png', { type: 'image/png' });
          resolve(file);
        },
        'image/png',
        1,
      ); // MIME type + quality (for JPEG)
    });
  },
  setIsCropping: (isCropping) => {
    set({ isCropping });
    if (isCropping) {
      setTimeout(() => {
        get().setImageCropperBackground();
      }, 100);
    }
  },
  setImage: (img) => set({ image: img }),
  setBackgroundColor: (color) => {
    set({ backgroundColor: color });
    get().setImageCropperBackground(color);
  },
  setCustomColor: (color) => set({ customColor: color }),
  rotateLeft: () => set((state) => ({ rotation: (state.rotation - 90) % 360 })),
  rotateRight: () => set((state) => ({ rotation: (state.rotation + 90) % 360 })),
  flipH: () => set((state) => ({ flipHorizontal: !state.flipHorizontal })),
  flipV: () => set((state) => ({ flipVertical: !state.flipVertical })),
  loadImage: (imageSource) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      set({ image: img });
    };
    if (typeof imageSource === 'string') {
      img.src = imageSource;
    } else {
      const url = URL.createObjectURL(imageSource);
      img.src = url;
    }
    set({ isCropping: true });
  },
  onCropApply: () => {
    const controls = get().controls;
    if (!controls) return;

    const dataUrl = controls.getCanvas()?.toDataURL();
    if (!dataUrl) return;

    const newImage = new window.Image();
    newImage.crossOrigin = 'anonymous';
    newImage.onload = () => set({ image: newImage });
    newImage.src = dataUrl;
    set({ isCropping: false });
  },
  resetTransforms: () =>
    set({
      rotation: 0,
      flipHorizontal: false,
      flipVertical: false,
      backgroundColor: 'transparent',
      customColor: '#000000',
      isCropping: false,
    }),
}));

export const useImage = createSelectors(imageSlice);
